import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Download,
  Refresh,
  Add,
  DeleteOutline,
  TrendingUp,
  TrendingDown,
  Assessment,
  ExpandMore,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useApp } from '../contexts/AppContext';
import { reportsService, ReportsMachinesResponse, MachineMaterialMapping } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { inputDay as toInputDate, reportBoundary, reportPreset } from '../utils/reportDates';

const ENCY_COLORS = ['#02B68D', '#4046D2', '#CC42EF', '#FFB640', '#22C55E', '#EF4444'];

const formatIsoDayLabel = (isoDay: string): string => {
  const d = new Date(`${isoDay}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDay;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

const buildMachineFilterLabel = (machine: any): string => {
  const customer = machine.companyName || 'Non assegnato';
  const reseller = machine.resellerName || 'Diretto';
  const serial = machine.serialNumber || '-';
  return `${machine.name} • SN: ${serial} • Cliente: ${customer} • Reseller: ${reseller}`;
};

type ProductionLinePoint = {
  day: string;
  produzioneMin: number;
  avviati: number;
};

const ProductionLineTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ProductionLinePoint | undefined;
  if (!point) return null;

  return (
    <Box sx={{ px: 1.5, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {formatIsoDayLabel(point.day)}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#02B68D' }}>
        Minuti produzione: {point.produzioneMin}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#4046D2' }}>
        Lavori avviati: {point.avviati}
      </Typography>
    </Box>
  );
};

const ReportsPage: React.FC = () => {
  const { state } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMachine, setSelectedMachine] = useState<string>(() => searchParams.get('machineId') || '');
  useEffect(() => { setSelectedMachine(searchParams.get('machineId') || ''); }, [searchParams]);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(() => reportPreset(7));
  const reportRequest = useRef(0);
  const invalidRange = !!(dateRange.start && dateRange.end && dateRange.start > dateRange.end);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportsMachinesResponse | null>(null);
  const [materialMappings, setMaterialMappings] = useState<MachineMaterialMapping[]>([]);
  const [materialSourceInput, setMaterialSourceInput] = useState('');
  const [materialMappedInput, setMaterialMappedInput] = useState('');
  const [materialMappingsLoading, setMaterialMappingsLoading] = useState(false);
  const [materialMappingsError, setMaterialMappingsError] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    const request = ++reportRequest.current;
    if (dateRange.start && dateRange.end && dateRange.start > dateRange.end) {
      setData(null); setError('La data di inizio deve precedere la data di fine.'); setLoading(false); return;
    }
    setLoading(true);
    setData(null);
    setError(null);
    try {
      const startDate = reportBoundary(dateRange.start);
      const endDate = reportBoundary(dateRange.end, true);
      const machineId = selectedMachine || undefined;

      const res = await reportsService.getMachinesReport({ machineId, startDate, endDate });
      if (request === reportRequest.current) setData(res);
    } catch (e: any) {
      if (request === reportRequest.current) setError(e?.response?.data?.error?.message || 'Errore caricamento report');
    } finally {
      if (request === reportRequest.current) setLoading(false);
    }
  }, [dateRange.end, dateRange.start, selectedMachine]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const loadMaterialMappings = useCallback(async () => {
    if (!selectedMachine) {
      setMaterialMappings([]);
      setMaterialMappingsError(null);
      return;
    }

    setMaterialMappingsLoading(true);
    setMaterialMappingsError(null);
    try {
      const rows = await reportsService.getMaterialMappings(selectedMachine);
      setMaterialMappings(rows);
    } catch (e: any) {
      setMaterialMappingsError(e?.response?.data?.error?.message || 'Errore caricamento associazioni materiale');
    } finally {
      setMaterialMappingsLoading(false);
    }
  }, [selectedMachine]);

  useEffect(() => {
    loadMaterialMappings();
  }, [loadMaterialMappings]);

  const saveMaterialMapping = useCallback(async () => {
    if (!selectedMachine) {
      setMaterialMappingsError("Seleziona una macchina prima di salvare un'associazione materiale");
      return;
    }

    const sourceMaterial = materialSourceInput.trim();
    const mappedMaterial = materialMappedInput.trim();

    if (!sourceMaterial || !mappedMaterial) {
      setMaterialMappingsError('Compila entrambi i campi: codice materiale e materiale associato');
      return;
    }

    setMaterialMappingsLoading(true);
    setMaterialMappingsError(null);
    try {
      await reportsService.upsertMaterialMapping({
        machineId: selectedMachine,
        sourceMaterial,
        mappedMaterial,
      });
      setMaterialSourceInput('');
      setMaterialMappedInput('');
      await Promise.all([loadMaterialMappings(), loadReports()]);
    } catch (e: any) {
      setMaterialMappingsError(e?.response?.data?.error?.message || 'Errore salvataggio associazione materiale');
    } finally {
      setMaterialMappingsLoading(false);
    }
  }, [selectedMachine, materialSourceInput, materialMappedInput, loadMaterialMappings, loadReports]);

  const removeMaterialMapping = useCallback(async (id: string) => {
    setMaterialMappingsLoading(true);
    setMaterialMappingsError(null);
    try {
      await reportsService.deleteMaterialMapping(id);
      await Promise.all([loadMaterialMappings(), loadReports()]);
    } catch (e: any) {
      setMaterialMappingsError(e?.response?.data?.error?.message || 'Errore eliminazione associazione materiale');
    } finally {
      setMaterialMappingsLoading(false);
    }
  }, [loadMaterialMappings, loadReports]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'default';
      case 'maintenance': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const summary = data?.summary;

  const byDayChartData = useMemo(() => {
    return (data?.byDay || []).map((d) => ({
      name: d.day,
      avviati: d.jobsStarted,
      completati: d.jobsCompleted,
      falliti: d.jobsFailed,
      cancellati: d.jobsCancelled,
    }));
  }, [data]);

  const productionLineData = useMemo(() => {
    return (data?.byDay || []).map((d) => ({
      day: d.day,
      produzioneMin: Math.round(d.productionMinutes),
      avviati: d.jobsStarted,
    }));
  }, [data]);

  const consumptionLineData = useMemo(() => {
    return (data?.byDay || []).map((d) => ({
      day: d.day,
      avgPowerKW: d.avgPowerKW ? +d.avgPowerKW.toFixed(2) : 0,
      avgGasM3: d.avgGasM3 ? +d.avgGasM3.toFixed(2) : 0,
    }));
  }, [data]);

  const alarmsChartData = useMemo(() => {
    return (data?.alarmsSummary || []).map((a) => ({
      name: a.title,
      count: a.count,
    }));
  }, [data]);

  const machinesById = useMemo(() => {
    const map = new Map<string, ReportsMachinesResponse['machines'][0]>();
    for (const m of data?.machines || []) map.set(m.machineId, m);
    return map;
  }, [data]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ['machineId', 'machineName', 'machineModel', 'productionHours', 'utilizationPercent', 'jobsStarted', 'jobsCompleted', 'jobsFailed', 'jobsCancelled', 'partsCompleted', 'totalParts'].join(','),
      ...data.machines.map((m) => [
        m.machineId,
        JSON.stringify(m.machineName),
        JSON.stringify(m.machineModel),
        (m.productionSeconds / 3600).toFixed(2),
        m.utilizationPercent.toFixed(1),
        m.jobsStarted,
        m.jobsCompleted,
        m.jobsFailed,
        m.jobsCancelled,
        m.partsCompleted,
        m.totalParts,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reports_machines_${(data.range.start || '').slice(0, 10)}_${(data.range.end || '').slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader title="Dati di produzione" subtitle="Analizza tempi, lavorazioni e utilizzo delle macchine. Scegli il periodo e confronta i risultati."
        actions={<Button variant="outlined" startIcon={<Download />} disabled={!data || loading} onClick={exportCsv}>Esporta CSV</Button>} />
      <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>Le <strong>ore di produzione</strong> misurano le lavorazioni; le <strong>ore accesa</strong> indicano la connessione dell’HMI. Le date comprendono l’intera giornata e i riepiloghi giornalieri usano l’ora UTC.</Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Periodo e macchine
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {[{ days: 1, label: 'Oggi' }, { days: 7, label: 'Ultimi 7 giorni' }, { days: 30, label: 'Ultimi 30 giorni' }].map(p => <Button key={p.days} size="small" variant="outlined" onClick={() => setDateRange(reportPreset(p.days))}>{p.label}</Button>)}
          </Stack>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="report-machine-label">Macchina</InputLabel>
                <Select
                  labelId="report-machine-label"
                  value={selectedMachine}
                  onChange={(e) => { setSelectedMachine(e.target.value); setSearchParams(e.target.value ? { machineId: e.target.value } : {}, { replace: true }); }}
                  label="Macchina"
                >
                  <MenuItem value="">Tutte le macchine</MenuItem>
                  {state.machines.map(machine => (
                    <MenuItem key={machine.id} value={machine.id}>
                      {buildMachineFilterLabel(machine)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Data Inizio"
                type="date"
                value={toInputDate(dateRange.start)}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value ? new Date(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Data Fine"
                type="date"
                value={toInputDate(dateRange.end)}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value ? new Date(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} /> : <Refresh />}
                onClick={loadReports}
                disabled={loading || invalidRange}
              >
                Aggiorna
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Accordion sx={{ mb: 3 }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMore />}><Typography fontWeight={600}>Configurazione materiali per macchina</Typography></AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Esempio: codice materiale <strong>202</strong> associato a <strong>ferro</strong> per una macchina specifica.
          </Typography>

          {!selectedMachine ? (
            <Alert severity="info">
              Seleziona una macchina nei filtri per gestire le associazioni materiale.
            </Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Codice materiale macchina"
                  placeholder="es. 202"
                  value={materialSourceInput}
                  onChange={(e) => setMaterialSourceInput(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Materiale associato"
                  placeholder="es. ferro"
                  value={materialMappedInput}
                  onChange={(e) => setMaterialMappedInput(e.target.value)}
                />
                <Button
                  variant="contained"
                  startIcon={materialMappingsLoading ? <CircularProgress size={18} /> : <Add />}
                  onClick={saveMaterialMapping}
                  disabled={materialMappingsLoading}
                >
                  Salva
                </Button>
              </Stack>

              {materialMappingsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {materialMappingsError}
                </Alert>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Codice macchina</TableCell>
                      <TableCell>Materiale associato</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materialMappings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          {materialMappingsLoading ? 'Caricamento...' : 'Nessuna associazione configurata'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      materialMappings.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.sourceMaterial}</TableCell>
                          <TableCell>{row.mappedMaterial}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeMaterialMapping(row.id)}
                              disabled={materialMappingsLoading}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </AccordionDetails>
      </Accordion>
      {loading && <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>Caricamento dei dati per il periodo selezionato…</Alert>}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {summary ? Math.round(summary.productionHoursTotal) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ore di produzione
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {summary ? `${Math.round(summary.averageUtilizationPercent)}%` : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Utilizzo medio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {summary ? Math.round(summary.onlineHoursTotal || 0) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ore accesa
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDown sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {summary ? Math.round(summary.offlineHoursTotal || 0) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ore spenta
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Andamento lavorazioni (per giorno)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byDayChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="avviati" fill="#4046D2" name="Avviati" />
                  <Bar dataKey="completati" fill="#02B68D" name="Completati" />
                  <Bar dataKey="falliti" fill="#EF4444" name="Falliti" />
                  <Bar dataKey="cancellati" fill="#FFB640" name="Cancellati" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Utilizzo materiale (n. nesting)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data?.materials || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {(data?.materials || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={ENCY_COLORS[index % ENCY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Produzione nel tempo
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={productionLineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={formatIsoDayLabel} />
                  <YAxis yAxisId="minutes" />
                  <YAxis yAxisId="jobs" orientation="right" allowDecimals={false} />
                  <Tooltip content={<ProductionLineTooltip />} />
                  <Legend />
                  <Line yAxisId="minutes" type="linear" dataKey="produzioneMin" stroke="#02B68D" name="Minuti produzione" />
                  <Line yAxisId="jobs" type="linear" dataKey="avviati" stroke="#4046D2" name="Lavori avviati" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Consumi Medi Giornalieri
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={consumptionLineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={formatIsoDayLabel} />
                  <YAxis yAxisId="kw" unit=" kW" />
                  <YAxis yAxisId="gas" orientation="right" unit=" m³" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'rgba(9,17,33,0.92)' }}
                    labelFormatter={(label) => formatIsoDayLabel(label as string)}
                  />
                  <Legend />
                  <Line yAxisId="kw" type="monotone" dataKey="avgPowerKW" stroke="#FFB640" name="Potenza Media (kW)" strokeWidth={2} />
                  <Line yAxisId="gas" type="monotone" dataKey="avgGasM3" stroke="#22C55E" name="Gas Medio (m³)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Allarmi Più Frequenti
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                {alarmsChartData.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={alarmsChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      dataKey="count"
                    >
                      {alarmsChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={ENCY_COLORS[index % ENCY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(9,17,33,0.92)' }} />
                  </PieChart>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="text.secondary">Nessun allarme nel periodo</Typography>
                  </Box>
                )}
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Nota sui dati
              </Typography>
              <Typography variant="body2" color="text.secondary">
                I dati di Produzione misurano lavori attivi (Nestings). I consumi sono le medie dei valori inviati via Telemetria.
                Gli allarmi visualizzano gli avvisi più emessi contabilizzati lato macchina.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Stato macchine e metriche
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={exportCsv}
                  disabled={!data}
                >
                  Esporta CSV
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Macchina</TableCell>
                      <TableCell>Modello</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Ore produzione</TableCell>
                      <TableCell>Ore accesa</TableCell>
                      <TableCell>Ore inattiva (accesa)</TableCell>
                      <TableCell>Ore spenta</TableCell>
                      <TableCell>Utilizzo</TableCell>
                      <TableCell>Lavori (OK/KO)</TableCell>
                      <TableCell>Ultimo contatto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.machines.filter(machine => !selectedMachine || machine.id === selectedMachine).map((machine) => {
                      const m = machinesById.get(machine.id);
                      const utilization = m?.utilizationPercent || 0;
                      const productionHours = m ? (m.productionSeconds / 3600) : null;
                      const onlineHours = m ? ((m.onlineSeconds || 0) / 3600) : null;
                      const idleHours = m ? ((m.idleOnSeconds || 0) / 3600) : null;
                      const offlineHours = m ? ((m.offlineSeconds || 0) / 3600) : null;
                      return (
                        <TableRow key={machine.id}>
                          <TableCell>{machine.name}</TableCell>
                          <TableCell>{machine.model}</TableCell>
                          <TableCell>
                            <Chip
                              label={{ online: 'Online', offline: 'Offline', maintenance: 'Manutenzione', error: 'Errore' }[machine.status] || machine.status}
                              color={getStatusColor(machine.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{productionHours === null ? '-' : `${productionHours.toFixed(1)}h`}</TableCell>
                          <TableCell>{onlineHours === null ? '-' : `${onlineHours.toFixed(1)}h`}</TableCell>
                          <TableCell>{idleHours === null ? '-' : `${idleHours.toFixed(1)}h`}</TableCell>
                          <TableCell>{offlineHours === null ? '-' : `${offlineHours.toFixed(1)}h`}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {m ? `${utilization.toFixed(0)}%` : '-'}
                              </Typography>
                              <Box sx={{ width: 60, height: 8, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 4 }}>
                                <Box
                                  sx={{
                                    width: `${utilization}%`,
                                    height: '100%',
                                    bgcolor: utilization > 80 ? 'success.main' : utilization > 60 ? 'warning.main' : 'error.main',
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {m ? `${m.jobsCompleted}/${m.jobsFailed}` : '-'}
                          </TableCell>
                          <TableCell>
                            {machine.lastSeen ? new Date(machine.lastSeen).toLocaleString() : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPage;
